/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace arcsine
*/
var arcsine = {};

/**
* @name cdf
* @memberof arcsine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/arcsine/cdf}
*/
import cdf from './../../../../base/dists/arcsine/cdf';
setReadOnly( arcsine, 'cdf', cdf );

/**
* @name Arcsine
* @memberof arcsine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/arcsine/ctor}
*/
import Arcsine from './../../../../base/dists/arcsine/ctor';
setReadOnly( arcsine, 'Arcsine', Arcsine );

/**
* @name entropy
* @memberof arcsine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/arcsine/entropy}
*/
import entropy from './../../../../base/dists/arcsine/entropy';
setReadOnly( arcsine, 'entropy', entropy );

/**
* @name kurtosis
* @memberof arcsine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/arcsine/kurtosis}
*/
import kurtosis from './../../../../base/dists/arcsine/kurtosis';
setReadOnly( arcsine, 'kurtosis', kurtosis );

/**
* @name logcdf
* @memberof arcsine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/arcsine/logcdf}
*/
import logcdf from './../../../../base/dists/arcsine/logcdf';
setReadOnly( arcsine, 'logcdf', logcdf );

/**
* @name logpdf
* @memberof arcsine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/arcsine/logpdf}
*/
import logpdf from './../../../../base/dists/arcsine/logpdf';
setReadOnly( arcsine, 'logpdf', logpdf );

/**
* @name mean
* @memberof arcsine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/arcsine/mean}
*/
import mean from './../../../../base/dists/arcsine/mean';
setReadOnly( arcsine, 'mean', mean );

/**
* @name median
* @memberof arcsine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/arcsine/median}
*/
import median from './../../../../base/dists/arcsine/median';
setReadOnly( arcsine, 'median', median );

/**
* @name mode
* @memberof arcsine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/arcsine/mode}
*/
import mode from './../../../../base/dists/arcsine/mode';
setReadOnly( arcsine, 'mode', mode );

/**
* @name pdf
* @memberof arcsine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/arcsine/pdf}
*/
import pdf from './../../../../base/dists/arcsine/pdf';
setReadOnly( arcsine, 'pdf', pdf );

/**
* @name quantile
* @memberof arcsine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/arcsine/quantile}
*/
import quantile from './../../../../base/dists/arcsine/quantile';
setReadOnly( arcsine, 'quantile', quantile );

/**
* @name skewness
* @memberof arcsine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/arcsine/skewness}
*/
import skewness from './../../../../base/dists/arcsine/skewness';
setReadOnly( arcsine, 'skewness', skewness );

/**
* @name stdev
* @memberof arcsine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/arcsine/stdev}
*/
import stdev from './../../../../base/dists/arcsine/stdev';
setReadOnly( arcsine, 'stdev', stdev );

/**
* @name variance
* @memberof arcsine
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/arcsine/variance}
*/
import variance from './../../../../base/dists/arcsine/variance';
setReadOnly( arcsine, 'variance', variance );


// EXPORTS //

export default arcsine;
